import React from 'react';
import { buildCanonicalArticleUrl } from '../../utils/buildArticleUrl.mjs';

/**
 * ShareOnX — Reusable "Share on X" button for article cards and detail pages.
 *
 * Opens the X (Twitter) share intent URL in a new window.
 * Follows the same interaction pattern as ChatButton (stopPropagation to avoid
 * triggering article link navigation).
 *
 * @param {Object} props
 * @param {Object} [props.article] - Article object (used with buildCanonicalArticleUrl)
 * @param {string} [props.url] - Direct URL override (takes precedence over article)
 * @param {string} [props.title] - Text to pre-fill in the tweet
 * @param {string} [props.variant] - 'card' (default) or 'detail' for different sizes
 */
export default function ShareOnX({ article, url, title, variant = 'card' }) {
    const handleShare = (e) => {
        e.stopPropagation();
        e.preventDefault();

        // Build the share URL
        let shareUrl = url;
        if (!shareUrl && article) {
            // Use window.location.origin on client, fall back to empty string on server
            const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
            shareUrl = buildCanonicalArticleUrl(article, baseUrl);
        }
        if (!shareUrl) return;

        // Build X intent URL
        const intentParams = new URLSearchParams();
        intentParams.set('url', shareUrl);
        if (title) {
            intentParams.set('text', title);
        }

        const intentUrl = `https://x.com/intent/post?${intentParams.toString()}`;

        // Open in a centered popup window (standard for share intents)
        const width = 550;
        const height = 420;
        const left = Math.round((window.screen.width - width) / 2);
        const top = Math.round((window.screen.height - height) / 2);
        window.open(
            intentUrl,
            'share-on-x',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );
    };

    return (
        <button
            onClick={handleShare}
            className={`share-on-x-button ${variant === 'detail' ? 'share-on-x-button--detail' : ''}`}
            aria-label="Share on X"
            title="Share on X"
        >
            {/* X (Twitter) logo SVG */}
            <svg
                className="share-on-x-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
                width="16"
                height="16"
            >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
        </button>
    );
}
